import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, X, Check } from "lucide-react";
import { getConsent, saveConsent, applyConsentToGtag } from "../../lib/consent";

const ConsentContext = createContext({ openSettings: () => {}, consent: null });
export const useConsent = () => useContext(ConsentContext);

function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
        checked ? "bg-gold" : "bg-stone/40"
      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-cream shadow transition-all ${
          checked ? "left-[22px]" : "left-0.5"
        }`}
      />
    </button>
  );
}

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(null);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      setConsent(existing);
      applyConsentToGtag(existing);
      setPrefs({ analytics: existing.analytics, marketing: existing.marketing });
    } else {
      setBannerOpen(true);
    }
  }, []);

  const commit = useCallback((choice) => {
    const saved = saveConsent(choice);
    setConsent(saved);
    setPrefs({ analytics: saved.analytics, marketing: saved.marketing });
    setBannerOpen(false);
    setModalOpen(false);
  }, []);

  const openSettings = useCallback(() => {
    const existing = getConsent();
    setPrefs(existing ? { analytics: existing.analytics, marketing: existing.marketing } : { analytics: false, marketing: false });
    setModalOpen(true);
  }, []);

  return (
    <ConsentContext.Provider value={{ consent, openSettings }}>
      {children}

      {/* Banner */}
      <AnimatePresence>
        {bannerOpen && !modalOpen && (
          <motion.div
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6"
            data-testid="cookie-banner"
          >
            <div className="mx-auto max-w-5xl overflow-hidden rounded-2xl border border-gold/30 bg-cream shadow-2xl">
              <div className="flex flex-col gap-5 p-6 md:flex-row md:items-center md:justify-between md:p-8">
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gold/15">
                    <Cookie className="h-5 w-5 text-gold-deep" />
                  </span>
                  <div>
                    <p className="font-serif text-xl text-ink">We value your privacy</p>
                    <p className="mt-1 max-w-2xl text-sm leading-relaxed text-stone">
                      We use strictly necessary cookies to run this site and, with your
                      permission, analytics cookies to understand how it's used. See our{" "}
                      <Link to="/cookies" className="underline hover:text-gold-deep">Cookie Policy</Link>{" "}and{" "}
                      <Link to="/privacy" className="underline hover:text-gold-deep">Privacy Notice</Link>.
                    </p>
                  </div>
                </div>
                <div className="flex flex-shrink-0 flex-col gap-2 sm:flex-row md:flex-col lg:flex-row">
                  <button
                    onClick={() => commit({ analytics: true, marketing: true })}
                    data-testid="cookie-accept-all"
                    className="rounded-full bg-gold px-6 py-2.5 text-sm font-medium text-charcoal transition hover:bg-gold-light"
                  >
                    Accept all
                  </button>
                  <button
                    onClick={() => commit({ analytics: false, marketing: false })}
                    data-testid="cookie-reject-optional"
                    className="rounded-full border border-charcoal/20 px-6 py-2.5 text-sm font-medium text-ink transition hover:border-charcoal/50"
                  >
                    Reject optional
                  </button>
                  <button
                    onClick={openSettings}
                    data-testid="cookie-manage"
                    className="rounded-full px-6 py-2.5 text-sm font-medium text-ink underline transition hover:text-gold-deep"
                  >
                    Manage preferences
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preferences modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            data-testid="cookie-preferences"
          >
            <div className="absolute inset-0 bg-charcoal/60 backdrop-blur-sm" onClick={() => setModalOpen(false)} />
            <motion.div
              initial={{ scale: 0.96, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 20 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-gold/30 bg-cream shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-gold/15 px-6 py-5">
                <h2 className="font-serif text-2xl text-ink">Cookie preferences</h2>
                <button aria-label="Close" onClick={() => setModalOpen(false)} className="text-stone hover:text-ink">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="max-h-[55vh] space-y-5 overflow-y-auto px-6 py-6">
                <div className="flex items-start justify-between gap-4 rounded-xl bg-cream-deep p-4">
                  <div>
                    <p className="font-medium text-ink">Strictly necessary</p>
                    <p className="mt-1 text-sm text-stone">Required for the site to work and stay secure. Always on.</p>
                  </div>
                  <Toggle checked disabled label="Strictly necessary (always on)" />
                </div>

                <div className="flex items-start justify-between gap-4 rounded-xl border border-gold/20 p-4">
                  <div>
                    <p className="font-medium text-ink">Analytics</p>
                    <p className="mt-1 text-sm text-stone">Google Analytics 4 — helps us understand how the site is used. If you decline, analytics cookies remain disabled.</p>
                  </div>
                  <Toggle
                    checked={prefs.analytics}
                    onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                    label="Analytics cookies"
                  />
                </div>

                <div className="flex items-start justify-between gap-4 rounded-xl border border-gold/20 p-4">
                  <div>
                    <p className="font-medium text-ink">Marketing / advertising</p>
                    <p className="mt-1 text-sm text-stone">Used for future advertising measurement. None are active yet.</p>
                  </div>
                  <Toggle
                    checked={prefs.marketing}
                    onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                    label="Marketing cookies"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2 border-t border-gold/15 px-6 py-5 sm:flex-row sm:justify-end">
                <button
                  onClick={() => commit({ analytics: false, marketing: false })}
                  className="rounded-full border border-charcoal/20 px-6 py-2.5 text-sm font-medium text-ink transition hover:border-charcoal/50"
                >
                  Reject optional
                </button>
                <button
                  onClick={() => commit(prefs)}
                  data-testid="cookie-save-preferences"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-charcoal px-6 py-2.5 text-sm font-medium text-cream transition hover:bg-charcoal-soft"
                >
                  <Check className="h-4 w-4" /> Save preferences
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ConsentContext.Provider>
  );
}
